<template>
  <section class="business-context-page tw-page">
    <div class="tw-container">
      <PageHeader
        title="Business Context"
        subtitle="Give Teamwrkr a concise picture of how your MSP operates so guidance, reporting, and reviews match your business."
      />

      <section class="context-hero">
        <div class="context-hero__copy">
          <p class="context-hero__eyebrow">Company intelligence</p>
          <h2>{{ heroTitle }}</h2>
          <p>{{ heroDescription }}</p>
        </div>

        <div class="context-hero__meta">
          <article class="meta-tile">
            <span>Current status</span>
            <strong>{{ heroStatus }}</strong>
          </article>
        </div>
      </section>

      <div v-if="isBootstrapping || isWaitingForUser" class="tw-state">
        <b-loading :is-full-page="false" :active="true" />
        <p>Loading your business context...</p>
      </div>

      <div v-else-if="pageError" class="tw-state tw-state--error">
        <p>{{ pageError }}</p>
        <b-button type="is-light" @click="retryInitialize">
          Retry
        </b-button>
      </div>

      <div v-else-if="userLoaded && !user?.company" class="tw-state tw-state--error">
        <p>No company is associated with your account.</p>
      </div>

      <div v-else class="context-layout">
        <section v-if="showEmptyState" class="empty-state tw-card">
          <div class="empty-state__copy">
            <p class="section-eyebrow">No context yet</p>
            <h3>No business context is available yet</h3>
            <p>
              Teamwrkr computes business context automatically from your ticket history.
              Check back once enough data has been ingested for your company.
            </p>
          </div>
        </section>

        <template v-else-if="showAdminContextView">
          <AdminContextView />
        </template>

        <template v-else-if="showApprovedContext">
          <section class="brief-shell">
            <article class="brief-card brief-card--hero tw-card">
              <div class="brief-card__header">
                <div>
                  <p class="section-eyebrow">Current approved context</p>
                  <h3>{{ approvedHeadline }}</h3>
                </div>
                <div class="brief-card__meta">
                  <span v-if="currentContext?.version">Version {{ currentContext.version }}</span>
                  <span v-if="currentContext?.updatedAt">
                    Updated {{ formatDate(currentContext.updatedAt) }}
                  </span>
                </div>
              </div>

              <p class="brief-card__narrative">
                {{ approvedNarrative }}
              </p>
            </article>

            <div class="brief-grid">
              <article
                v-for="section in approvedSections"
                :key="section.key"
                class="brief-card tw-card"
              >
                <p class="section-eyebrow">{{ section.kicker }}</p>
                <h4>{{ section.title }}</h4>
                <p>{{ section.body }}</p>
              </article>
            </div>

            <article v-if="approvedHighlights.length" class="brief-card tw-card">
              <p class="section-eyebrow">Quick highlights</p>
              <div class="token-list">
                <span v-for="item in approvedHighlights" :key="item" class="token-pill">
                  {{ item }}
                </span>
              </div>
            </article>
          </section>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'pinia'
import PageHeader from '@/components/layout/PageHeader.vue'
import AdminContextView from '@/components/company-context/AdminContextView.vue'
import { useUserStore } from '@/stores/userStore'
import {
  fetchCurrentCompanyContext,
} from '@/services/companyContextService'

const SECTION_KICKERS = {
  business: 'About your business',
  business_profile: 'About your business',
  service: 'Service model',
  service_model: 'Service model',
  technology: 'Technology and delivery',
  technology_delivery: 'Technology and delivery',
  delivery: 'Technology and delivery',
  guardrails: 'Avoid recommending',
  avoid_recommending: 'Avoid recommending',
  reporting: 'Reporting preferences',
  reporting_preferences: 'Reporting preferences',
}

export default {
  name: 'BusinessContext',
  components: {
    PageHeader,
    AdminContextView,
  },
  data() {
    return {
      isBootstrapping: false,
      pageError: null,
      currentContext: null,
      initializedCompanyId: null,
    }
  },
  computed: {
    ...mapState(useUserStore, ['user']),
    userLoaded() {
      return !!this.user?._id
    },
    isWaitingForUser() {
      return !this.userLoaded && !!this.getToken()
    },
    showEmptyState() {
      return !this.currentContext
    },
    // Story 17.8 — a nightly-inferred context gets the read + amendment admin view.
    showAdminContextView() {
      return this.currentContext?.source === 'inferred'
    },
    showApprovedContext() {
      return !!this.currentContext && this.currentContext?.source !== 'inferred'
    },
    heroTitle() {
      if (this.showAdminContextView) return 'Your computed customer context'
      if (this.showApprovedContext) return 'Your approved business context'
      return 'No business context yet'
    },
    heroDescription() {
      if (this.showAdminContextView) {
        return 'Teamwrkr computes this Customer Profile and Operating Baseline from your ticket data every night. Add a note on anything that looks off — it’s a passive annotation and won’t change the computed value.'
      }
      if (this.showApprovedContext) {
        return 'This is the approved business context Teamwrkr currently uses for your company.'
      }
      return 'Teamwrkr computes business context automatically from your ticket history once enough data is available.'
    },
    heroStatus() {
      if (this.showAdminContextView) return 'Inferred'
      if (this.showApprovedContext) return 'Approved'
      return 'Not available'
    },
    approvedHeadline() {
      return this.currentContext?.summary?.headline || 'Approved business context'
    },
    approvedNarrative() {
      return this.currentContext?.summary?.narrative || 'No summary is available for the approved context.'
    },
    approvedSections() {
      return this.buildBriefSections(this.currentContext?.summary)
    },
    approvedHighlights() {
      return this.collectSummaryHighlights(this.currentContext?.summary)
    },
  },
  watch: {
    user: {
      immediate: true,
      async handler(nextUser) {
        if (!nextUser?._id) return
        const companyId = typeof nextUser.company === 'object'
          ? nextUser.company?._id
          : nextUser.company

        if (!companyId) {
          this.pageError = 'No company is associated with your account.'
          return
        }
        if (this.initializedCompanyId === companyId) return
        await this.initializePage(companyId)
      },
    },
  },
  created() {
    const userStore = useUserStore()
    const token = this.getToken()

    if (token && !this.user?._id) {
      userStore.fetchUser(token)
    }
  },
  methods: {
    emptySummary() {
      return {
        headline: '',
        narrative: '',
        sections: [],
        overview: [],
        guardrails: [],
        reportingPriorities: [],
        openQuestions: [],
        reviewPrompt: '',
      }
    },
    getToken() {
      return this.$cookies.get('teamwrkr-token')
    },
    formatDate(value) {
      if (!value) return '—'
      return this.$moment(value).format('MMM D, YYYY')
    },
    extractErrorMessage(error, fallbackMessage) {
      return error?.response?.data?.message || fallbackMessage
    },
    async initializePage(companyId) {
      this.initializedCompanyId = companyId
      this.isBootstrapping = true
      this.pageError = null
      this.currentContext = null

      try {
        const token = this.getToken()
        const currentResponse = await fetchCurrentCompanyContext(this.axios, token)
        this.currentContext = currentResponse?.companyContext || null
      } catch (error) {
        this.pageError = this.extractErrorMessage(
          error,
          'Unable to load business context.',
        )
      } finally {
        this.isBootstrapping = false
      }
    },
    async retryInitialize() {
      const companyId = typeof this.user?.company === 'object'
        ? this.user.company?._id
        : this.user?.company

      if (!companyId) return
      await this.initializePage(companyId)
    },
    buildBriefSections(summary = this.emptySummary()) {
      const sections = Array.isArray(summary?.sections) ? summary.sections : []
      const filteredSections = sections
        .filter((section) => this.isCustomerFacingSection(section))
        .map((section) => ({
          key: section.key || section.title,
          title: section.title,
          body: section.body,
          kicker: this.getSectionKicker(section),
        }))

      if (filteredSections.length) return filteredSections

      const fallbackSections = []
      if (summary?.overview?.length) {
        fallbackSections.push({
          key: 'overview',
          title: 'About your business',
          body: summary.overview.join(' '),
          kicker: 'About your business',
        })
      }
      if (summary?.guardrails?.length) {
        fallbackSections.push({
          key: 'avoid-recommending',
          title: 'Avoid recommending',
          body: summary.guardrails.join(' '),
          kicker: 'Avoid recommending',
        })
      }
      if (summary?.reportingPriorities?.length) {
        fallbackSections.push({
          key: 'reporting-preferences',
          title: 'Reporting preferences',
          body: summary.reportingPriorities.join(' '),
          kicker: 'Reporting preferences',
        })
      }

      return fallbackSections
    },
    isCustomerFacingSection(section) {
      const haystack = `${section?.key || ''} ${section?.title || ''}`.toLowerCase()
      const hiddenTerms = ['semantic', 'archetype', 'similarity', 'internal', 'note']
      return !!section?.body && !hiddenTerms.some((term) => haystack.includes(term))
    },
    getSectionKicker(section) {
      const haystack = `${section?.key || ''} ${section?.title || ''}`.toLowerCase()
      const matchedKey = Object.keys(SECTION_KICKERS).find((key) => haystack.includes(key))
      return SECTION_KICKERS[matchedKey] || section?.title || 'Business context'
    },
    collectSummaryHighlights(summary = this.emptySummary()) {
      const items = [
        ...(summary?.overview || []),
        ...(summary?.reportingPriorities || []),
      ]

      return [...new Set(items)].slice(0, 8)
    },
  },
}
</script>

<style lang="scss" scoped>
.business-context-page {
  background:
    radial-gradient(circle at top left, rgba(230, 70, 45, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(170, 118, 214, 0.14), transparent 32%),
    linear-gradient(180deg, #fcfbff 0%, $color-surface 18%);
}

.context-layout {
  display: grid;
  gap: $space-5;
}

.context-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(280px, 0.9fr);
  gap: $space-5;
  padding: $space-6;
  margin-bottom: $space-5;
  border-radius: 24px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.96) 0%, rgba(249, 245, 255, 0.96) 58%, rgba(245, 248, 255, 0.96) 100%);
  border: 1px solid rgba(170, 118, 214, 0.18);
  box-shadow: $shadow-md;
}

.context-hero__copy h2 {
  margin: 0 0 $space-3;
  font-size: clamp(1.9rem, 3vw, 2.9rem);
}

.context-hero__copy p:last-child {
  margin: 0;
  max-width: 58ch;
  color: $color-text-muted;
  font-size: 1.02rem;
}

.context-hero__eyebrow,
.section-eyebrow {
  margin: 0 0 $space-2;
  color: $color-primary-dark;
  font-size: $font-size-xs;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.context-hero__meta {
  display: grid;
  gap: $space-3;
  align-content: start;
}

.meta-tile {
  padding: $space-4;
  border-radius: $radius-lg;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(170, 118, 214, 0.12);
}

.meta-tile span {
  display: block;
  margin-bottom: $space-1;
  color: $color-text-muted;
  font-size: $font-size-sm;
}

.meta-tile strong {
  color: $color-text-strong;
  font-size: $font-size-lg;
}

.brief-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: $space-4;
  flex-wrap: wrap;
}

.brief-card__header h3 {
  margin: 0;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-6;
  background: linear-gradient(145deg, $color-surface 0%, $color-surface-soft 100%);
}

.empty-state__copy h3 {
  margin: 0 0 $space-2;
}

.empty-state__copy p:last-child {
  margin: 0;
  color: $color-text-muted;
  max-width: 56ch;
}

.brief-shell {
  display: grid;
  gap: $space-5;
}

.brief-card--hero {
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 255, 0.98) 100%);
  border: 1px solid rgba(30, 80, 162, 0.12);
}

.brief-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
  color: $color-text-muted;
  font-size: $font-size-sm;
}

.brief-card__narrative {
  margin: $space-4 0 0;
  color: $color-text;
  font-size: 1.05rem;
  line-height: 1.8;
  white-space: pre-wrap;
}

.brief-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-4;
}

.brief-card h4 {
  margin: 0 0 $space-3;
}

.brief-card p:last-child {
  margin-bottom: 0;
  color: $color-text-muted;
  white-space: pre-wrap;
}

.token-list {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
}

.token-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.8rem;
  border-radius: $radius-pill;
  background: $color-surface-soft;
  border: 1px solid $color-border;
  color: $color-text;
  font-size: $font-size-sm;
}

@media (max-width: 960px) {
  .context-hero,
  .brief-grid,
  .empty-state {
    grid-template-columns: 1fr;
  }

  .empty-state {
    align-items: flex-start;
  }
}
</style>
